// Client session: WebSocket signalling + mediasoup-client media, with
// automatic reconnection. Reactive state (Svelte 5 runes) drives every page.

import { Device } from 'mediasoup-client';
import type { types as msc } from 'mediasoup-client';
import type { CallInfo, CallTarget, ConsumerOffer, Events, Methods } from '../../../shared/src/protocol';
import type {
  BrowserSourceInfo,
  ClientStats,
  ParticipantInfo,
  PositionConfig,
  PositionTally,
  Role,
  SourceHealth,
  TalkTarget,
} from '../../../shared/src/types';
import { AudioOut } from './audio';
import { ClockSync } from './latency';
import { Signalling } from './signalling';

export type ConnStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error' | 'kicked';
type JoinResult = Methods['join']['result'];
type ProviderStatus = Events['tallyProviders']['providers'][number];

const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 10000];

export interface SessionOptions {
  positionId: string | null;
  /** Talk-capable roles get a microphone stream from the "Tap to start" gesture. */
  mic: MediaStream | null;
  listenOnly?: boolean;
  /** Pass an AudioOut already unlocked inside the start gesture. */
  audio?: AudioOut;
}

export class Session {
  status = $state<ConnStatus>('idle');
  error = $state<string | null>(null);
  attempt = $state(0);
  me = $state<{ id: string; name: string; role: Role } | null>(null);
  canTalk = $state(false);
  position = $state<PositionConfig | null>(null);
  positions = $state<PositionConfig[]>([]);
  groups = $state<string[]>([]);
  scope = $state<'all' | 'group'>('all');
  tally = $state<Record<string, PositionTally>>({});
  sources = $state<Record<string, SourceHealth>>({});
  talkers = $state<Events['talkers']['talkers']>([]);
  calls = $state<CallInfo[]>([]);
  participants = $state<ParticipantInfo[]>([]);
  providers = $state<ProviderStatus[]>([]);
  sourceId = $state<string | null>(null);
  sourceName = $state<string | null>(null);
  videoStream = $state<MediaStream | null>(null);
  thumbStreams = $state<Record<string, MediaStream>>({});
  /** Call grid: other participants' browser cameras, by source id. */
  peerStreams = $state<Record<string, MediaStream>>({});
  browserSources = $state<BrowserSourceInfo[]>([]);
  /** The browser camera source this client may publish (null = not allowed). */
  videoSourceId = $state<string | null>(null);
  cameraOn = $state(false);
  cameraError = $state<string | null>(null);
  facing = $state<'user' | 'environment'>('user');
  localVideo = $state<MediaStream | null>(null);
  talking = $state(false);
  talkTarget = $state<TalkTarget | null>(null);
  micLive = $state(false);
  lowBandwidth = $state(false);
  stats = $state<ClientStats>({
    rttMs: null,
    lossPct: null,
    videoKbps: null,
    fps: null,
    jitterMs: null,
    measuredLatencyMs: null,
    playoutDelayMs: null,
  });

  readonly audio: AudioOut;
  readonly clock = new ClockSync();
  private sig: Signalling | null = null;
  private device: Device | null = null;
  private sendT: msc.Transport | null = null;
  private recvT: msc.Transport | null = null;
  private consumers = new Map<string, { consumer: msc.Consumer; offer: ConsumerOffer }>();
  private producer: msc.Producer | null = null;
  private camProducer: msc.Producer | null = null;
  private progProducer: msc.Producer | null = null;
  /** Camera tracks survive reconnects so the picture comes straight back. */
  private camTrack: MediaStreamTrack | null = null;
  private progTrack: MediaStreamTrack | null = null;
  private camSendAudio = true;
  private timers: number[] = [];
  private reconnectTimer: number | null = null;
  private stopped = false;
  private consumeChain: Promise<unknown> = Promise.resolve();
  private lastBytes: { video: number; at: number; lost: number; recv: number } | null = null;
  /** Raw counters from the last stats run, for debugging (window.__vtb). */
  lastInbound: Record<string, number> | null = null;
  private iceRestarting = false;
  private thumbsWanted: string[] = [];

  constructor(private opts: SessionOptions) {
    this.audio = opts.audio ?? new AudioOut();
    window.addEventListener('online', this.onOnline);
  }

  // --------------------------------------------------------- lifecycle

  async start(): Promise<void> {
    this.stopped = false;
    this.status = this.attempt === 0 ? 'connecting' : 'reconnecting';
    try {
      await this.connect();
      this.status = 'connected';
      this.error = null;
      this.attempt = 0;
    } catch (err) {
      this.cleanup();
      if (this.stopped || (this.status as ConnStatus) === 'kicked') return;
      this.error = (err as Error).message;
      // Configuration errors (position in use, revoked) won't fix themselves quickly but
      // we keep trying: the admin may free the position. Back off harder though.
      this.scheduleReconnect();
    }
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener('online', this.onOnline);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.cleanup();
    this.releaseCamera();
    this.audio.clear();
    this.status = 'idle';
  }

  private onOnline = () => {
    if (this.status === 'reconnecting' && this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      void this.start();
    }
  };

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.status = 'reconnecting';
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)];
    this.attempt++;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.start();
    }, delay);
  }

  private cleanup(): void {
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    const sig = this.sig;
    this.sig = null;
    if (sig) {
      sig.onclose = null;
      sig.close();
    }
    for (const { consumer } of this.consumers.values()) consumer.close();
    this.consumers.clear();
    this.producer?.close();
    this.producer = null;
    this.camProducer?.close();
    this.progProducer?.close();
    this.camProducer = this.progProducer = null;
    this.sendT?.close();
    this.recvT?.close();
    this.sendT = this.recvT = null;
    this.audio.clear();
    this.videoStream = null;
    this.thumbStreams = {};
    this.peerStreams = {};
    this.talking = false;
    this.micLive = false;
    this.lastBytes = null;
  }

  private async connect(): Promise<void> {
    const sig = new Signalling();
    this.sig = sig;
    this.bindEvents(sig);
    await sig.connect();
    sig.onclose = (code, reason) => {
      if (code === 4001 || this.status === 'kicked') {
        this.status = 'kicked';
        this.error = reason || 'Disconnected';
        this.cleanup();
        return;
      }
      this.cleanup();
      this.scheduleReconnect();
    };

    const join: JoinResult = await sig.request('join', {
      positionId: this.opts.positionId,
      listenOnly: this.opts.listenOnly,
    });
    this.applyJoin(join);
    void this.syncClock();
    this.timers.push(window.setInterval(() => void this.syncClock(1), 30_000));

    const device = new Device();
    await device.load({ routerRtpCapabilities: join.routerRtpCapabilities });
    this.device = device;

    this.recvT = await this.createTransport('recv');
    await sig.request('setRtpCapabilities', { rtpCapabilities: device.recvRtpCapabilities });
    if (this.lowBandwidth) await sig.request('setLowBandwidth', { enabled: true });
    if (this.thumbsWanted.length) await sig.request('watchThumbs', { sourceIds: this.thumbsWanted });

    const track = this.opts.mic?.getAudioTracks()[0];
    if (join.canTalk && track && track.readyState === 'live' && device.canProduce('audio')) {
      this.sendT = await this.createTransport('send');
      this.producer = await this.sendT.produce({
        track,
        // Zero RTP while paused: nothing on the wire unless PTT is held.
        zeroRtpOnPause: true,
        disableTrackOnPause: true,
        stopTracks: false,
        codecOptions: { opusStereo: false, opusDtx: false, opusFec: true, opusPtime: 10 },
        appData: { role: 'talk' },
      });
      this.producer.pause();
      this.micLive = true;
    }
    // Camera was on before a reconnect: publish it again straight away.
    if (this.cameraOn && this.camTrack?.readyState === 'live') await this.publishCamera().catch((e) => (this.cameraError = (e as Error).message));
    this.timers.push(window.setInterval(() => void this.collectStats(), 2000));
  }

  private applyJoin(j: JoinResult): void {
    this.me = { id: j.participantId, name: j.name, role: j.role };
    this.canTalk = j.canTalk;
    this.videoSourceId = j.videoSourceId;
    this.position = j.position;
    this.positions = j.positions;
    this.groups = j.groups;
    this.scope = j.scope;
    this.audio.duckGain = Math.pow(10, j.duckDb / 20);
  }

  private async createTransport(direction: 'send' | 'recv'): Promise<msc.Transport> {
    const sig = this.sig!;
    const params = await sig.request('createTransport', { direction });
    const opts = { ...params, iceServers: [], iceTransportPolicy: 'all' as const };
    const t = direction === 'send' ? this.device!.createSendTransport(opts) : this.device!.createRecvTransport(opts);
    t.on('connect', ({ dtlsParameters }, cb, errb) => {
      sig.request('connectTransport', { transportId: t.id, dtlsParameters }).then(() => cb(), errb);
    });
    if (direction === 'send') {
      t.on('produce', ({ kind, rtpParameters, appData }, cb, errb) => {
        const a = appData as { role?: 'talk' | 'camera' | 'program-audio'; width?: number; height?: number };
        sig
          .request('produce', { transportId: t.id, kind, rtpParameters, role: a.role ?? 'talk', width: a.width, height: a.height })
          .then((r) => cb({ id: r.id }), errb);
      });
    }
    t.on('connectionstatechange', (state) => this.onTransportState(t, state));
    return t;
  }

  /** LAN-only ICE: on 'disconnected' give it a moment, then ICE-restart; on repeated failure reconnect. */
  private onTransportState(t: msc.Transport, state: string): void {
    if (state === 'connected') {
      this.iceRestarting = false;
      return;
    }
    if (state === 'disconnected') {
      window.setTimeout(() => {
        if (t.connectionState === 'disconnected' && !t.closed) void this.restartIce(t);
      }, 2000);
    } else if (state === 'failed') {
      if (this.iceRestarting) {
        this.sig?.close();
        this.cleanup();
        this.scheduleReconnect();
      } else void this.restartIce(t);
    }
  }

  private async restartIce(t: msc.Transport): Promise<void> {
    if (!this.sig) return;
    this.iceRestarting = true;
    try {
      const iceParameters = await this.sig.request('restartIce', { transportId: t.id });
      await t.restartIce({ iceParameters });
    } catch {
      this.cleanup();
      this.scheduleReconnect();
    }
  }

  // ------------------------------------------------------------- events

  private bindEvents(sig: Signalling): void {
    sig.on('consumer:new', (offer) => {
      this.consumeChain = this.consumeChain.then(() => this.consume(offer)).catch((err) => console.warn('consume failed', err));
    });
    sig.on('consumer:closed', ({ consumerId }) => {
      this.consumeChain = this.consumeChain.then(() => this.dropConsumer(consumerId));
    });
    sig.on('tally', ({ positions, full }) => {
      this.tally = full ? positions : { ...this.tally, ...positions };
    });
    sig.on('sources', ({ health }) => (this.sources = health));
    sig.on('talkers', ({ talkers }) => (this.talkers = talkers));
    sig.on('calls', ({ calls }) => (this.calls = calls));
    sig.on('participants', ({ participants }) => (this.participants = participants));
    sig.on('tallyProviders', ({ providers }) => (this.providers = providers));
    sig.on('browserSources', ({ sources }) => (this.browserSources = sources));
    sig.on('position:update', ({ position, positions }) => {
      this.positions = positions;
      if (this.position) this.position = position;
    });
    sig.on('source:assigned', ({ sourceId, sourceName }) => {
      this.sourceId = sourceId;
      this.sourceName = sourceName;
    });
    sig.on('config:audio', ({ scope, groups, duckDb }) => {
      this.scope = scope;
      this.groups = groups;
      this.audio.duckGain = Math.pow(10, duckDb / 20);
    });
    sig.on('kicked', ({ reason }) => {
      this.status = 'kicked';
      this.error = reason;
    });
  }

  private async consume(offer: ConsumerOffer): Promise<void> {
    if (!this.recvT || this.recvT.closed) return;
    const consumer = await this.recvT.consume({
      id: offer.consumerId,
      producerId: offer.producerId,
      kind: offer.kind,
      rtpParameters: offer.rtpParameters,
    });
    this.consumers.set(offer.consumerId, { consumer, offer });
    minimiseJitterBuffer(consumer.rtpReceiver);
    const track = consumer.track;
    switch (offer.role) {
      case 'video':
        this.videoStream = new MediaStream([track]);
        this.lastBytes = null;
        break;
      case 'thumb':
        if (offer.sourceId) this.thumbStreams = { ...this.thumbStreams, [offer.sourceId]: new MediaStream([track]) };
        break;
      case 'peer':
        if (offer.sourceId) this.peerStreams = { ...this.peerStreams, [offer.sourceId]: new MediaStream([track]) };
        break;
      case 'return-audio':
        this.audio.add(offer.consumerId, 'return', track);
        break;
      case 'talk':
        this.audio.add(offer.consumerId, 'talk', track);
        break;
    }
    await this.sig?.request('consumerReady', { consumerId: offer.consumerId });
  }

  private dropConsumer(id: string): void {
    const c = this.consumers.get(id);
    if (!c) return;
    this.consumers.delete(id);
    c.consumer.close();
    if (c.offer.role === 'video' && this.videoStream?.getVideoTracks()[0] === c.consumer.track) this.videoStream = null;
    if (c.offer.role === 'thumb' && c.offer.sourceId && this.thumbStreams[c.offer.sourceId]?.getVideoTracks()[0] === c.consumer.track) {
      const { [c.offer.sourceId]: _gone, ...rest } = this.thumbStreams;
      this.thumbStreams = rest;
    }
    if (c.offer.role === 'peer' && c.offer.sourceId && this.peerStreams[c.offer.sourceId]?.getVideoTracks()[0] === c.consumer.track) {
      const { [c.offer.sourceId]: _gone, ...rest } = this.peerStreams;
      this.peerStreams = rest;
    }
    this.audio.remove(id);
  }

  // ------------------------------------------------------------ actions

  /** Push-to-talk. Local mute/unmute is immediate; the server opens routes in parallel. */
  async setTalking(active: boolean, target: TalkTarget = { kind: 'partyline' }): Promise<void> {
    if (!this.producer || !this.sig) return;
    if (active === this.talking && JSON.stringify(target) === JSON.stringify(this.talkTarget)) return;
    this.talking = active;
    this.talkTarget = active ? target : null;
    this.audio.setDucked(active);
    if (active) this.producer.resume();
    else this.producer.pause();
    try {
      await this.sig.request('talk', { active, target });
    } catch (err) {
      this.error = (err as Error).message;
      if (active) {
        this.producer?.pause();
        this.talking = false;
        this.audio.setDucked(false);
      }
    }
  }

  async call(active: boolean, to: CallTarget): Promise<void> {
    await this.sig?.request('call', { active, to });
  }

  async callAck(fromId: string): Promise<void> {
    await this.sig?.request('callAck', { fromId });
  }

  async setLowBandwidth(enabled: boolean): Promise<void> {
    this.lowBandwidth = enabled;
    await this.sig?.request('setLowBandwidth', { enabled });
  }

  async watchThumbs(sourceIds: string[]): Promise<void> {
    this.thumbsWanted = sourceIds;
    if (this.sig?.open && this.recvT) await this.sig.request('watchThumbs', { sourceIds });
  }

  // ------------------------------------------------------------- camera

  /**
   * Start sending this device's camera (and, by default, an always-on programme
   * mic, separate from push-to-talk). Must be called from a user gesture on iOS.
   */
  async startCamera(opts: { sendAudio?: boolean; facing?: 'user' | 'environment' } = {}): Promise<void> {
    if (!this.videoSourceId) throw new Error('This position cannot send video');
    this.cameraError = null;
    this.camSendAudio = opts.sendAudio ?? true;
    if (opts.facing) this.facing = opts.facing;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 }, facingMode: this.facing },
        audio: this.camSendAudio && !this.opts.mic ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true } : false,
      });
      this.releaseCamera();
      this.camTrack = stream.getVideoTracks()[0];
      this.camTrack.contentHint = 'motion';
      const mic = this.opts.mic?.getAudioTracks()[0] ?? stream.getAudioTracks()[0];
      // A clone: the talkback producer pauses its track on PTT release, this one stays open.
      this.progTrack = this.camSendAudio && mic ? mic.clone() : null;
      if (this.progTrack) this.progTrack.enabled = true;
      this.localVideo = new MediaStream([this.camTrack]);
      this.cameraOn = true;
      if (this.status === 'connected') await this.publishCamera();
    } catch (err) {
      this.cameraError = (err as Error).message;
      this.cameraOn = false;
      throw err;
    }
  }

  async stopCamera(): Promise<void> {
    this.cameraOn = false;
    this.camProducer?.close();
    this.progProducer?.close();
    this.camProducer = this.progProducer = null;
    this.releaseCamera();
  }

  /** Phones: swap front/back camera without dropping the call. */
  async flipCamera(): Promise<void> {
    if (!this.cameraOn) return;
    const facing = this.facing === 'user' ? 'environment' : 'user';
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 }, facingMode: facing },
    });
    const track = stream.getVideoTracks()[0];
    this.camTrack?.stop();
    this.camTrack = track;
    this.facing = facing;
    this.localVideo = new MediaStream([track]);
    await this.camProducer?.replaceTrack({ track });
  }

  private releaseCamera(): void {
    this.camTrack?.stop();
    this.progTrack?.stop();
    this.camTrack = this.progTrack = null;
    this.localVideo = null;
  }

  private async publishCamera(): Promise<void> {
    if (!this.device || !this.camTrack) return;
    if (!this.sendT) this.sendT = await this.createTransport('send');
    const caps = this.device.rtpCapabilities.codecs ?? [];
    // Prefer H.264 (hardware encode on phones); VP8 where the browser has no H.264 encoder.
    const h264 = caps.find((c) => c.mimeType.toLowerCase() === 'video/h264' && String(c.parameters?.['profile-level-id'] ?? '').startsWith('42e0'));
    const vp8 = caps.find((c) => c.mimeType.toLowerCase() === 'video/vp8');
    const codec = h264 ?? vp8;
    const settings = this.camTrack.getSettings();
    this.camProducer?.close();
    this.camProducer = await this.sendT.produce({
      track: this.camTrack,
      codec,
      // Two simulcast layers: full for big tiles/outputs, quarter-size for thumbnails and Wi-Fi.
      encodings: [
        { rid: 'r0', scaleResolutionDownBy: 2, maxBitrate: 400_000, scalabilityMode: 'L1T1' },
        { rid: 'r1', scaleResolutionDownBy: 1, maxBitrate: 2_500_000, scalabilityMode: 'L1T1' },
      ],
      codecOptions: { videoGoogleStartBitrate: 1000 },
      stopTracks: false,
      appData: { role: 'camera', width: settings.width, height: settings.height },
    });
    if (this.progTrack) {
      this.progProducer?.close();
      this.progProducer = await this.sendT.produce({
        track: this.progTrack,
        stopTracks: false,
        codecOptions: { opusStereo: false, opusDtx: false, opusFec: true, opusPtime: 10 },
        appData: { role: 'program-audio' },
      });
    }
  }

  /** Per-consumer receive counters: diagnostics and automated tests. */
  async consumerStats(): Promise<Array<{ role: string; participantId?: string; kind: string; bytes: number; packets: number }>> {
    const out = [];
    for (const { consumer, offer } of this.consumers.values()) {
      let bytes = 0;
      let packets = 0;
      (await consumer.getStats()).forEach((s: { type: string; bytesReceived?: number; packetsReceived?: number }) => {
        if (s.type === 'inbound-rtp') {
          bytes += s.bytesReceived ?? 0;
          packets += s.packetsReceived ?? 0;
        }
      });
      out.push({ role: offer.role, participantId: offer.participantId, kind: offer.kind, bytes, packets });
    }
    return out;
  }

  setMeasuredLatency(ms: number | null): void {
    this.stats.measuredLatencyMs = ms;
  }

  // ------------------------------------------------------ clock & stats

  private async syncClock(rounds = 5): Promise<void> {
    for (let i = 0; i < rounds && this.sig?.open; i++) {
      const t0 = Date.now();
      try {
        const r = await this.sig.request('time', { t0 });
        this.clock.add(t0, r.server, Date.now());
      } catch {
        return;
      }
      await new Promise((r) => setTimeout(r, 150));
    }
  }

  private async collectStats(): Promise<void> {
    const t = this.recvT;
    if (!t || t.closed || !this.sig?.open) return;
    let report: RTCStatsReport;
    try {
      report = await t.getStats();
    } catch {
      return;
    }
    let rtt: number | null = null;
    let video: Record<string, number> | null = null;
    report.forEach((s: Record<string, unknown> & { type: string }) => {
      if (s.type === 'candidate-pair' && (s.nominated || s.state === 'succeeded') && typeof s.currentRoundTripTime === 'number') {
        rtt = s.currentRoundTripTime * 1000;
      }
      if (s.type === 'inbound-rtp' && s.kind === 'video') video = s as unknown as Record<string, number>;
    });
    const st: ClientStats = { ...this.stats, rttMs: rtt };
    if (video) {
      const v = video as Record<string, number>;
      const now = performance.now();
      const lost = v.packetsLost ?? 0;
      const recv = v.packetsReceived ?? 0;
      if (this.lastBytes) {
        st.videoKbps = Math.round(((v.bytesReceived - this.lastBytes.video) * 8) / (now - this.lastBytes.at));
        // Loss over the last interval, not since connect: what the director needs is "now".
        const dl = Math.max(0, lost - this.lastBytes.lost);
        const dr = Math.max(0, recv - this.lastBytes.recv);
        st.lossPct = dl + dr > 0 ? Math.round((dl / (dl + dr)) * 1000) / 10 : 0;
      }
      this.lastBytes = { video: v.bytesReceived, at: now, lost, recv };
      this.lastInbound = v;
      st.fps = v.framesPerSecond ?? null;
      st.jitterMs = typeof v.jitter === 'number' ? Math.round(v.jitter * 1000) : null;
      // Browser's own estimate: average jitter-buffer delay + decode time per frame.
      if (v.jitterBufferEmittedCount > 0) {
        const jb = (v.jitterBufferDelay / v.jitterBufferEmittedCount) * 1000;
        const dec = v.framesDecoded > 0 ? (v.totalDecodeTime / v.framesDecoded) * 1000 : 0;
        st.playoutDelayMs = Math.round(jb + dec);
      }
    } else {
      st.videoKbps = st.fps = st.jitterMs = st.lossPct = st.playoutDelayMs = null;
    }
    this.stats = st;
    void this.sig.request('stats', st).catch(() => undefined);
  }
}

/** Ask the browser for the smallest jitter buffer it allows (LAN: jitter is tiny). */
function minimiseJitterBuffer(receiver: RTCRtpReceiver | undefined): void {
  if (!receiver) return;
  const r = receiver as RTCRtpReceiver & { jitterBufferTarget?: number | null; playoutDelayHint?: number };
  try {
    if ('jitterBufferTarget' in r) r.jitterBufferTarget = 0; // ms (Chrome 114+, Firefox, Safari 18)
  } catch {
    /* unsupported */
  }
  try {
    r.playoutDelayHint = 0; // seconds (older Chrome)
  } catch {
    /* unsupported */
  }
}
